/* MindMorn product page: gallery, pack and plan choice, delivery estimate, add to cart, sticky bar.
   Variants are Pack x Purchase (One-time / Subscribe & save). Subscribers' delivery interval goes
   into the cart as a line item property. Adds through Horizon's CartLinesUpdateEvent so the drawer
   opens and the header count updates. */
(() => {
  const root = document.querySelector('.mm-pdp');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';

  const cfg = JSON.parse(root.querySelector('[data-mm-config]').textContent);
  const ROOT = window.Shopify?.routes?.root || '/';

  /* ---------- money in the shop's format, without trailing zeros ---------- */
  function money(cents) {
    const fmt = cfg.moneyFormat || 'Rs {{amount_no_decimals_with_comma_separator}}';
    const n = Number(cents) / 100;
    const group = (v, sep) => v.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const whole = Math.round(n).toString();
    const map = {
      amount: group(n.toFixed(2).replace(/\.00$/, ''), ','),
      amount_no_decimals: group(whole, ','),
      amount_with_comma_separator: group(n.toFixed(2).replace('.', ','), '.').replace(/,00$/, ''),
      amount_no_decimals_with_comma_separator: group(whole, ','),
      amount_no_decimals_with_space_separator: group(whole, ' '),
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => map[k] ?? map.amount);
  }

  /* ---------- gallery ---------- */
  const track = root.querySelector('[data-mm-track]');
  const thumbs = [...root.querySelectorAll('[data-mm-thumb]')];
  const slides = track ? [...track.children] : [];
  const goTo = (i) => {
    const s = slides[i];
    if (!s) return;
    track.scrollTo({ left: s.offsetLeft - track.offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  thumbs.forEach((t) => t.addEventListener('click', () => goTo(+t.dataset.mmThumb)));
  if (slides.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.6) {
          const i = slides.indexOf(e.target);
          thumbs.forEach((t, j) => t.classList.toggle('is-on', j === i));
        }
      });
    }, { root: track, threshold: [0.6] });
    slides.forEach((s) => io.observe(s));
  }

  /* ---------- state ---------- */
  const packInputs = [...root.querySelectorAll('[data-mm-packs] input[type=radio]')];
  const modeInputs = [...root.querySelectorAll('[data-mm-mode] input[type=radio]')];
  const freqButtons = [...root.querySelectorAll('.mm-freq__opt')];
  const sticky = root.querySelector('[data-mm-sticky]');
  const stickyPack = root.querySelector('[data-mm-sticky-pack]');
  const stickyPlan = root.querySelector('[data-mm-sticky-plan]');
  let freq = freqButtons[0] ? +freqButtons[0].dataset.days : null;

  const pack = () => (packInputs.find((i) => i.checked) || packInputs[0])?.value;
  const mode = () => (cfg.hasSub ? (modeInputs.find((i) => i.checked) || modeInputs[0])?.value : null);
  const find = (p, m) => cfg.variants.find((v) => v.pack === p && (!cfg.hasSub || v.plan === m)) || cfg.variants.find((v) => v.pack === p);
  const current = () => find(pack(), mode());

  /* ---------- savings, always from live prices (never the stored compare-at price) ----------
     Reference = the smallest pack bought one-time, scaled by size: 30 shots vs 2 x 15, 2 bottles
     vs 2 x 1, Subscribe & save vs one-time. Change any price in Shopify and every saving follows. */
  const unitsOf = (packName) => parseInt(String(packName), 10) || 1;
  const baseVariant = (() => {
    const pool = cfg.variants.filter((v) => !cfg.hasSub || v.plan === cfg.oneTime);
    return (pool.length ? pool : cfg.variants).reduce((a, b) => (unitsOf(b.pack) < unitsOf(a.pack) ? b : a));
  })();
  const ref = (v) => Math.round((baseVariant.price * unitsOf(v.pack)) / unitsOf(baseVariant.pack));
  const isSub = () => cfg.hasSub && mode() === cfg.subscribe;

  function render() {
    const p = pack();
    const m = mode();
    const v = current();
    if (!v) return;

    // pack cards: price shown for the chosen plan, saving, per-day
    packInputs.forEach((input, idx) => {
      const card = input.closest('.mm-plan');
      card.classList.toggle('is-on', input.checked);
      const pv = find(input.value, m);
      if (!pv) return;
      card.querySelector('[data-plan-price]').textContent = money(pv.price);
      const was = card.querySelector('[data-plan-was]');
      const save = card.querySelector('[data-plan-save]');
      const pref = ref(pv);
      const off = pref > pv.price;
      was.hidden = !off;
      save.hidden = !off;
      if (off) {
        was.textContent = money(pref);
        save.textContent = `Save ${money(pref - pv.price)}`;
      }
      card.querySelectorAll('[data-perk-save]').forEach((el) => { el.textContent = off ? money(pref - pv.price) : ''; });
      const day = card.querySelector('[data-plan-day]');
      if (day && cfg.daysPerUnit > 0) {
        const units = +card.dataset.units || idx + 1;
        day.textContent = `${money(Math.round(pv.price / (units * cfg.daysPerUnit) / 100) * 100)} / day`;
      }
    });

    // mode cards: price of the chosen pack in each plan
    modeInputs.forEach((input) => input.closest('.mm-plan').classList.toggle('is-on', input.checked));
    const subPrice = root.querySelector('[data-mode-price="sub"]');
    const onePrice = root.querySelector('[data-mode-price="one"]');
    if (subPrice) subPrice.textContent = money(find(p, cfg.subscribe)?.price ?? v.price);
    if (onePrice) onePrice.textContent = money(find(p, cfg.oneTime)?.price ?? v.price);
    // "Save 10%" on the subscribe card: the live gap between the two plans for this pack
    const subBadge = root.querySelector('[data-sub-badge]');
    const sv = find(p, cfg.subscribe);
    const ov = find(p, cfg.oneTime);
    if (subBadge && sv && ov && ov.price > sv.price) {
      subBadge.hidden = false;
      subBadge.textContent = `Save ${Math.round(((ov.price - sv.price) / ov.price) * 100)}%`;
    } else if (subBadge && sv && ov) {
      subBadge.hidden = true;
    }

    // total (the Total box was removed from the page; this still works if it comes back)
    const total = root.querySelector('.mm-total');
    if (total) {
      total.querySelector('[data-now]').textContent = money(v.price);
      const was = total.querySelector('[data-was]');
      const save = total.querySelector('[data-save]');
      const vref = ref(v);
      const off = vref > v.price;
      was.hidden = !off;
      save.hidden = !off;
      if (off) {
        was.textContent = money(vref);
        save.textContent = `Save ${Math.round(((vref - v.price) / vref) * 100)}%`;
      }
    }

    root.querySelector('[data-atc-price]').textContent = money(v.price);
    const sp = root.querySelector('[data-sticky-price]');
    if (sp) sp.textContent = money(v.price);
    if (stickyPack) stickyPack.value = p;
    const spn = root.querySelector('[data-sticky-pack-name]');
    if (spn) spn.textContent = p;
    if (stickyPlan && m) stickyPlan.value = m;

    const door = root.querySelector('[data-mm-door]');
    if (door) door.innerHTML = door.dataset.text.replace('[price]', `<strong>${money(v.price)}</strong>`);
    const wa = root.querySelector('[data-mm-wa]');
    if (wa) {
      const plan = isSub() ? `${cfg.subscribe}, every ${freq} days` : (cfg.hasSub ? cfg.oneTime : '');
      const msg = wa.dataset.msg.replace('[pack]', p).replace('[plan]', plan).replace('[price]', money(v.price)).replace(/, ,/g, ',');
      wa.href = `${wa.dataset.base}?text=${encodeURIComponent(msg)}`;
    }

    // the gallery follows the chosen pack once the shopper picks one
    if (root.dataset.userPicked && v.media) {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(v.media));
      if (i >= 0) goTo(i);
    }

    const url = new URL(location.href);
    url.searchParams.set('variant', v.id);
    history.replaceState(history.state, '', url);
  }

  packInputs.forEach((i) => i.addEventListener('change', () => { root.dataset.userPicked = '1'; render(); }));
  modeInputs.forEach((i) => i.addEventListener('change', render));
  freqButtons.forEach((b) => b.addEventListener('click', (e) => {
    e.preventDefault();
    freq = +b.dataset.days;
    freqButtons.forEach((x) => { x.classList.toggle('is-on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
    const sub = modeInputs.find((i) => i.value === cfg.subscribe);
    if (sub && !sub.checked) sub.checked = true;
    render();
  }));
  stickyPack?.addEventListener('change', () => {
    packInputs.forEach((i) => { i.checked = i.value === stickyPack.value; });
    root.dataset.userPicked = '1';
    render();
  });
  stickyPlan?.addEventListener('change', () => {
    modeInputs.forEach((i) => { i.checked = i.value === stickyPlan.value; });
    render();
  });

  // ?variant= from ads or the homepage
  const wanted = new URL(location.href).searchParams.get('variant');
  const wv = wanted && cfg.variants.find((v) => String(v.id) === wanted);
  if (wv) {
    packInputs.forEach((i) => { i.checked = i.value === wv.pack; });
    if (cfg.hasSub) modeInputs.forEach((i) => { i.checked = i.value === wv.plan; });
  }
  render();

  /* ---------- delivery estimate in Pakistan time, Monday to Saturday ---------- */
  const eta = root.querySelector('[data-mm-eta]');
  if (eta) {
    const min = +eta.dataset.min || 2;
    const max = +eta.dataset.max || 5;
    const cutoff = +eta.dataset.cutoff || 15;
    const pkt = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
    const start = new Date(pkt);
    start.setHours(0, 0, 0, 0);
    const sunday = (d) => d.getDay() === 0;
    if (pkt.getHours() >= cutoff || sunday(start)) {
      do { start.setDate(start.getDate() + 1); } while (sunday(start));
    }
    const add = (from, days) => {
      const d = new Date(from);
      let left = days;
      while (left > 0) { d.setDate(d.getDate() + 1); if (!sunday(d)) left--; }
      return d;
    };
    const fmt = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    eta.querySelector('span').innerHTML = `Order now, delivered <strong>${fmt(add(start, min))} to ${fmt(add(start, max))}</strong>`;
  }

  /* ---------- add to cart ---------- */
  const err = root.querySelector('[data-mm-error]');
  function post(body) {
    return fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json()).then((j) => { if (j.status) throw j; return j; });
  }
  function lineItems() {
    const v = current();
    const item = { id: v.id, quantity: 1 };
    if (isSub() && freq) item.properties = { [cfg.frequencyProperty]: `${freq} days` };
    return [item];
  }
  async function add(btn, thenCheckout) {
    const v = current();
    if (!v) return;
    const label = btn.querySelector('[data-atc-label], .mm-sticky__btn-label') || btn;
    const original = label.textContent;
    err.hidden = true;
    btn.disabled = true;
    label.textContent = cfg.addingLabel;
    const items = lineItems();
    try {
      if (thenCheckout) {
        await post({ items });
        location.href = `${ROOT}checkout`;
        return;
      }
      const ids = [...document.querySelectorAll('cart-items-component')].map((e) => e.dataset.sectionId).filter(Boolean);
      let ev = null;
      try { ev = await import('@shopify/events'); } catch (e) { ev = null; }
      if (!ev || !ids.length) {
        await post({ items });
        location.href = `${ROOT}cart`;
        return;
      }
      const d = ev.CartLinesUpdateEvent.createPromise();
      root.dispatchEvent(new ev.CartLinesUpdateEvent({
        action: 'add', context: 'product', promise: d.promise,
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      }));
      const settle = async (sections, failed) => {
        const cart = await fetch(`${ROOT}cart.js`).then((r) => r.json());
        d.resolve({
          cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'mm-product', sourceId: 'mm-product', itemCount: 1, sections, didError: failed },
        });
      };
      try {
        const res = await post({ items, sections: ids.join(',') });
        await settle(res.sections, false);
      } catch (e) {
        await settle(undefined, true);
        throw e;
      }
      label.textContent = cfg.addedLabel;
      setTimeout(() => { label.textContent = original; }, 1600);
    } catch (e) {
      label.textContent = original;
      err.textContent = (e && (e.description || e.message)) || 'That did not work. Please try again.';
      err.hidden = false;
    } finally {
      btn.disabled = false;
    }
  }
  const atc = root.querySelector('[data-mm-atc]');
  atc?.addEventListener('click', () => add(atc, false));
  root.querySelector('[data-mm-buynow]')?.addEventListener('click', (e) => add(e.currentTarget, true));
  root.querySelector('[data-mm-sticky-atc]')?.addEventListener('click', (e) => add(e.currentTarget, false));
  // Sticky "Choose your pack": scroll back up to the pack options and focus the chosen one.
  root.querySelector('[data-mm-sticky-choose]')?.addEventListener('click', () => {
    const packs = root.querySelector('[data-mm-packs]') || root.querySelector('.mm-buy');
    if (!packs) return;
    packs.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const picked = packs.querySelector('input:checked') || packs.querySelector('input');
    if (picked) setTimeout(() => picked.focus({ preventScroll: true }), 600);
  });

  /* ---------- sticky bar: only once the whole product section has scrolled past (above the
     viewport), never over the footer. Hidden by CSS while the cart drawer or menu is open. ---------- */
  if (sticky) {
    const footer = document.querySelector('.mm-foot, footer');
    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const pastProduct = root.getBoundingClientRect().bottom < 0;
      const footerIn = footer ? footer.getBoundingClientRect().top < vh : false;
      const show = pastProduct && !footerIn;
      if (sticky.classList.contains('is-on') === show) return;
      sticky.classList.toggle('is-on', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.inert = !show;
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    // Capture: Horizon scrolls .page-wrapper on desktop and the window on phones; this hears both.
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }
})();
